import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { BlueprintGrid, CrossMarker } from "../components/BlueprintGrid";
import { Link } from "react-router-dom";
import { scrollToSection, LOCATION_COORDINATE_META } from "../lib/site";
import { HERO_PLAN_IMG, HERO_CONCEPT_IMG } from "../lib/homevision";
import { lineMask, EASE } from "../lib/motion";

const LINES = ["SEE YOUR HOME", "BEFORE YOU", "BUILD IT."];

const VisionHero = () => (
    <section
        data-testid="vision-hero"
        className="relative flex min-h-[100svh] flex-col overflow-hidden bg-ink"
    >
        <BlueprintGrid />
        <CrossMarker className="absolute left-10 top-28 hidden text-white/25 md:block" />

        <div className="relative z-10 mx-auto flex w-full max-w-[1600px] flex-1 items-center px-6 pb-16 pt-36 md:px-10 lg:px-16">
            <div className="grid w-full items-center gap-14 lg:grid-cols-12">
                <div className="lg:col-span-5">
                    <motion.p
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
                        className="flex items-center gap-3 font-mono text-[13px] tracking-[0.3em] text-mist"
                    >
                        <span className="h-1.5 w-1.5 bg-accent" />
                        HOME VISION
                    </motion.p>

                    <h1
                        data-testid="vision-hero-headline"
                        className="mt-6 font-display text-[clamp(2.5rem,5.5vw,5.5rem)] font-bold leading-[1.05] text-white"
                    >
                        {LINES.map((line, i) => (
                            <span
                                key={line}
                                className="-mb-[0.08em] block overflow-hidden pb-[0.08em]"
                            >
                                <motion.span
                                    className="block"
                                    variants={lineMask}
                                    initial="hidden"
                                    animate="show"
                                    custom={i}
                                >
                                    {i === 2 ? (
                                        <>
                                            BUILD IT
                                            <span className="text-accent">
                                                .
                                            </span>
                                        </>
                                    ) : (
                                        line
                                    )}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: EASE, delay: 0.75 }}
                        className="mt-8 max-w-lg text-[16px] leading-relaxed text-mist md:text-base"
                    >
                        Turn your architectural plan into an AI-generated
                        visual concept and explore how your home could look
                        before construction begins.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: EASE, delay: 0.9 }}
                        className="mt-10 flex flex-wrap items-center gap-x-9 gap-y-5"
                    >
                        <Link
                            to="/join-the-pilot"
                            data-testid="vision-hero-join-pilot"
                            className="inline-flex items-center gap-2 bg-accent px-7 py-4 text-sm font-semibold tracking-wide text-white transition-colors duration-300 hover:bg-[#ff7026]"
                        >
                            Join the Pilot
                            <ArrowUpRight className="h-4 w-4" />
                        </Link>
                        <a
                            href="#frames"
                            data-testid="vision-hero-see-concepts"
                            onClick={(e) => {
                                e.preventDefault();
                                scrollToSection("#frames");
                            }}
                            className="group inline-flex items-center gap-2 text-sm tracking-wide text-white"
                        >
                            See the Concepts
                            <span className="text-accent transition-transform duration-300 group-hover:translate-y-1">
                                ↓
                            </span>
                        </a>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.1, ease: EASE, delay: 0.4 }}
                    className="lg:col-span-7"
                >
                    <div
                        data-testid="vision-hero-diptych"
                        className="relative overflow-hidden border border-white/10 bg-surface"
                    >
                        <div className="relative aspect-[4/3] sm:aspect-[16/10]">
                            <motion.img
                                src={HERO_CONCEPT_IMG}
                                alt="Visual concept of a modern home"
                                initial={{ scale: 1.08 }}
                                animate={{ scale: 1 }}
                                transition={{ duration: 2.4, ease: EASE }}
                                className="absolute inset-0 h-full w-full object-cover"
                            />
                            <motion.div
                                initial={{ clipPath: "inset(0 100% 0 0)" }}
                                animate={{ clipPath: "inset(0 0% 0 0)" }}
                                transition={{ duration: 1.5, ease: EASE, delay: 0.7 }}
                                className="absolute inset-y-0 left-0 w-[58%]"
                            >
                                <img
                                    src={HERO_PLAN_IMG}
                                    alt="Architectural plan drawing"
                                    className="h-full w-full object-cover"
                                />
                                <div className="absolute inset-0 bg-ink/45" />
                                <div className="blueprint-bg absolute inset-0 opacity-70" />
                            </motion.div>

                            <motion.span
                                initial={{ opacity: 0, scaleY: 0 }}
                                animate={{ opacity: 1, scaleY: 1 }}
                                transition={{ duration: 0.8, ease: EASE, delay: 2.1 }}
                                className="absolute inset-y-0 left-[58%] w-px origin-top bg-accent"
                            />
                            <motion.span
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.6, delay: 2.4 }}
                                className="absolute left-[58%] top-4 -translate-x-1/2 bg-ink/80 px-2 py-1 font-mono text-[13px] tracking-[0.22em] text-accent backdrop-blur-sm"
                            >
                                HV. 01
                            </motion.span>

                            <p className="absolute bottom-4 left-4 bg-ink/70 px-2.5 py-1.5 font-mono text-[13px] tracking-[0.2em] text-white/85 backdrop-blur-sm">
                                INPUT —{" "}
                                <span className="hidden sm:inline">
                                    ARCHITECTURAL{" "}
                                </span>
                                PLAN
                            </p>
                            <p className="absolute bottom-4 right-4 bg-ink/70 px-2.5 py-1.5 text-right font-mono text-[13px] tracking-[0.2em] text-white/85 backdrop-blur-sm">
                                OUTPUT — <span className="hidden sm:inline">AI </span>
                                CONCEPT
                                <span className="hidden sm:inline"> VISUAL</span>
                            </p>
                        </div>
                    </div>
                    <div className="mt-4 flex items-center justify-between font-mono text-[13px] tracking-[0.2em] text-cad">
                        <span>DWG. A-101 → HV. RENDER.01</span>
                    </div>
                </motion.div>
            </div>
        </div>

        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="relative z-10 border-t border-white/10"
            data-testid="vision-metadata-bar"
        >
            <div className="mx-auto flex max-w-[1600px] items-center justify-between gap-6 px-6 py-4 font-mono text-[13px] tracking-[0.24em] text-mist/80 md:px-10 lg:px-16">
                <span className="hidden sm:block">BUILD INTELLIGENCE</span>
                <span data-testid="vision-stage-counter">HOME VISION</span>
                <span>SOUTH AFRICA</span>
                <span className="hidden md:block">{LOCATION_COORDINATE_META}</span>
            </div>
        </motion.div>
    </section>
);

export default VisionHero;
